import { call, put, takeLatest, fork, select } from 'redux-saga/effects';
import axios from 'src/api/Api';
import { logUiErrorToBackend } from 'src/utils/logUiErrorToBackend';
import { errorHandler } from 'src/errorHandler/errorHandler';
import { getAlertsApiEndpoint } from './endpoints';
import {
    DELETE_ALERTS,
    DELETE_ALERTS_FAILURE,
    DELETE_ALERTS_SUCCESS,
    GET_ALERTS,
    GET_ALERTS_FAILURE,
    GET_ALERTS_SUCCESS,
    MARK_ALERTS_READ,
    MARK_ALERTS_READ_FAILURE,
    MARK_ALERTS_READ_SUCCESS,
    GET_FILTER_DISTRICTS,
    GET_FILTER_DISTRICTS_SUCCESS,
    GET_FILTER_DISTRICTS_FAILURE,
    GET_FILTER_TERRITORIES,
    GET_FILTER_TERRITORIES_SUCCESS,
    GET_FILTER_TERRITORIES_FAILURE,
    GET_FILTER_ACCOUNTS,
    GET_FILTER_ACCOUNTS_SUCCESS,
    GET_FILTER_ACCOUNTS_FAILURE,
    SET_FILTER_ALERT,
} from 'src/pages/alerts/redux/action';

const deleteAlertsApiEndpoint = () => {
    const baseSiteId = localStorage.getItem('baseSiteId');
    return baseSiteId
        ? `/occ/v2/${baseSiteId}/alerts/removeAlerts`
        : 'alerts/removeAlerts';
};

const markAlertsReadApiEndpoint = () => {
    const baseSiteId = localStorage.getItem('baseSiteId');
    return baseSiteId
        ? `/occ/v2/${baseSiteId}/alerts/markAlertsRead`
        : 'alerts/markAlertsRead';
};

const alertsDistrictsApiEndpoint = () => {
    const baseSiteId = localStorage.getItem('baseSiteId');
    return baseSiteId
        ? `/occ/v2/${baseSiteId}/alerts/alertsDistricts`
        : 'alerts/alertsDistricts';
};

const alertsTerritoriesApiEndpoint = () => {
    const baseSiteId = localStorage.getItem('baseSiteId');
    return baseSiteId
        ? `/occ/v2/${baseSiteId}/alerts/alertsTerritories`
        : 'alerts/alertsTerritories';
};

const alertsAccountsApiEndpoint = () => {
    const baseSiteId = localStorage.getItem('baseSiteId');
    return baseSiteId
        ? `/occ/v2/${baseSiteId}/alerts/alertsAccounts`
        : 'alerts/alertsAccounts';
};

export function* getAlertsSaga(action) {
    const url = getAlertsApiEndpoint();
    const requestPayload = action?.payload ?? {};
    const {
        pageNum = 0,
        pageSize = 50,
        sortBy = '',
        sortOrder = 'ASC',
        districtCode,
        territory,
        account,
        alertStatusFilter,
        categories,
    } = requestPayload;

    try {
        const response = yield call(axios.get, url, {
            params: {
                pageNum,
                pageSize,
                sortBy,
                sortOrder,
                districtCode,
                territory,
                account,
                alertStatusFilter,
                categories,
            },
        });

        const payload = response?.data ?? {};
        const data = payload?.data ?? payload;
        const alertData = data?.alerts ?? [];

        yield put({
            type: GET_ALERTS_SUCCESS,
            payload: {
                alerts: Array.isArray(alertData) ? alertData : [],
                totalResults: data?.totalResults ?? payload?.totalResults ?? 0,
                totalUnreadCount: data?.totalUnreadCount ?? payload?.totalUnreadCount ?? 0,
            },
        });
    } catch (error) {
        logUiErrorToBackend(error, {
            apiEndPoint: url,
            requestPayload,
        });

        yield put({
            type: GET_ALERTS_FAILURE,
            error: errorHandler('503') ?? 'Unable to load alerts',
        });
    }
}

export function* deleteAlertsSaga(action) {
    const url = deleteAlertsApiEndpoint();
    const requestPayload = action?.payload ?? {};
    const notificationIds = Array.isArray(requestPayload?.notificationIds)
        ? requestPayload.notificationIds
              .map((id) => `${id}`.trim())
              .filter(Boolean)
        : [];
    const refreshPayload = requestPayload?.refreshPayload ?? {};

    if (notificationIds.length === 0) {
        yield put({
            type: DELETE_ALERTS_SUCCESS,
            payload: { notificationIds: [] },
        });
        yield put({
            type: GET_ALERTS,
            payload: refreshPayload,
        });
        return;
    }

    try {
        yield call(axios.delete, url, {
            data: { notificationIds },
        });

        yield put({
            type: DELETE_ALERTS_SUCCESS,
            payload: { notificationIds },
        });

        yield put({
            type: GET_ALERTS,
            payload: refreshPayload,
        });
    } catch (error) {
        logUiErrorToBackend(error, {
            apiEndPoint: url,
            requestPayload: { notificationIds },
        });

        yield put({
            type: DELETE_ALERTS_FAILURE,
            error: errorHandler('503') ?? 'Unable to delete alerts',
        });
    }
}

export function* markAlertsReadSaga(action) {
    const url = markAlertsReadApiEndpoint();
    const requestPayload = action?.payload ?? {};
    const notificationIds = Array.isArray(requestPayload?.notificationIds)
        ? requestPayload.notificationIds
              .map((id) => `${id}`.trim())
              .filter(Boolean)
        : [];
    const refreshPayload = requestPayload?.refreshPayload ?? {};

    if (notificationIds.length === 0) {
        yield put({
            type: MARK_ALERTS_READ_SUCCESS,
            payload: { notificationIds: [] },
        });
        yield put({
            type: GET_ALERTS,
            payload: refreshPayload,
        });
        return;
    }

    try {
        yield call(axios.put, url, {
            notificationIds,
        });

        yield put({
            type: MARK_ALERTS_READ_SUCCESS,
            payload: { notificationIds },
        });

        yield put({
            type: GET_ALERTS,
            payload: refreshPayload,
        });
    } catch (error) {
        logUiErrorToBackend(error, {
            apiEndPoint: url,
            requestPayload: { notificationIds },
        });

        yield put({
            type: MARK_ALERTS_READ_FAILURE,
            error: errorHandler('503') ?? 'Unable to mark alerts as read',
        });
    }
}

const getDefaultFilterCodes = (list = [], codeKey = 'filterCode') =>
    (Array.isArray(list) ? list : [])
        .filter((item) => item?.isDefault && item?.[codeKey] && item[codeKey] !== 'All')
        .map((item) => item[codeKey])
        .join(',');

// Kept as a flat array (not joined) so it matches the appliedFilterNames shape used elsewhere
const getDefaultFilterNames = (list = [], codeKey = 'filterName') =>
    (Array.isArray(list) ? list : [])
        .filter((item) => item?.isDefault && item?.[codeKey] && item[codeKey] !== 'All')
        .map((item) => item[codeKey]);

export const buildAlertsPayloadFromDistrictResponse = (payload = {}) => {
    const districtList = Array.isArray(payload)
        ? payload
        : payload?.districts ?? [];
    const setAlertsGroup = payload?.setAlertsGroup ?? [];
    const setCategoriesList = payload?.setCategoriesList ?? [];
    const currentPayload = {
        districtCode: districtList.length > 1 ? getDefaultFilterCodes(districtList) : '', 
        alertStatusFilter: getDefaultFilterCodes(setAlertsGroup),
        categories: getDefaultFilterCodes(setCategoriesList),
        // pageSize: 25,
        sortBy: 'alertDate',
        sortOrder: 'ASC',
    };
    const cleanedData = Object.fromEntries(
        Object.entries(currentPayload).filter(([_, value]) => value !== "")
    );

    return cleanedData;
};

const ALERT_FILTER_KEYS = [
    'districtCode',
    'territory',
    'account',
    'alertStatusFilter',
    'categories',
];

export const getAppliedAlertsPayload = (appliedAlertFilters = {}) =>
    Object.fromEntries(
        ALERT_FILTER_KEYS
            .filter((key) => appliedAlertFilters[key])
            .map((key) => [key, appliedAlertFilters[key]])
    );

function* fetchFilterData({ action, url, successType, failureType }) {
    const requestPayload = action?.payload ?? {};

    try {
        const response = yield call(axios.get, url, { params: requestPayload });
        const payload = response?.data ?? [];

        yield put({
            type: successType,
            payload,
        });

        return payload;
    } catch (error) {
        logUiErrorToBackend(error, {
            apiEndPoint: url,
            requestPayload,
        });

        yield put({
            type: failureType,
            error: errorHandler('503') ?? 'Unable to load filter data',
        });

        return null;
    }
}

export function* getFilterDistrictsSaga(action) {
    const payload = yield* fetchFilterData({
        action,
        url: alertsDistrictsApiEndpoint(),
        successType: GET_FILTER_DISTRICTS_SUCCESS,
        failureType: GET_FILTER_DISTRICTS_FAILURE,
    });

    if (payload == null) {
        return;
    }

    const currentPayload = buildAlertsPayloadFromDistrictResponse(payload);
    const defaultFilterNames = getDefaultFilterNames(payload?.setCategoriesList);
    const appliedAlertFilters = yield select(
        (state) => state.alertsReducer?.appliedAlertFilters ?? {}
    );
    const appliedPayload = getAppliedAlertsPayload(appliedAlertFilters);
    const hasAppliedFilters = Object.keys(appliedPayload).length > 0;
    const alertsPayload = hasAppliedFilters
        ? appliedPayload 
        : currentPayload;

    yield put({
        type: GET_ALERTS,
        payload: alertsPayload,
    });

    if (hasAppliedFilters) {
        return;
    }

    yield put({
        type: SET_FILTER_ALERT,
        payload: { appliedFilterNames: defaultFilterNames },
    });
}

export function* getFilterTerritoriesSaga(action) {
    yield* fetchFilterData({
        action,
        url: alertsTerritoriesApiEndpoint(),
        successType: GET_FILTER_TERRITORIES_SUCCESS,
        failureType: GET_FILTER_TERRITORIES_FAILURE,
    });
}

export function* getFilterAccountsSaga(action) {
    yield* fetchFilterData({
        action,
        url: alertsAccountsApiEndpoint(),
        successType: GET_FILTER_ACCOUNTS_SUCCESS,
        failureType: GET_FILTER_ACCOUNTS_FAILURE,
    });
}

function* watchGetAlerts() {
    yield takeLatest(GET_ALERTS, getAlertsSaga);
}

function* watchDeleteAlerts() {
    yield takeLatest(DELETE_ALERTS, deleteAlertsSaga);
}

function* watchMarkAlertsRead() {
    yield takeLatest(MARK_ALERTS_READ, markAlertsReadSaga);
}

function* watchGetFilterDistricts() {
    yield takeLatest(GET_FILTER_DISTRICTS, getFilterDistrictsSaga);
}

function* watchGetFilterTerritories() {
    yield takeLatest(GET_FILTER_TERRITORIES, getFilterTerritoriesSaga);
}

function* watchGetFilterAccounts() {
    yield takeLatest(GET_FILTER_ACCOUNTS, getFilterAccountsSaga);
}

export default function* alertsRootSaga() {
    yield fork(watchGetAlerts);
    yield fork(watchDeleteAlerts);
    yield fork(watchMarkAlertsRead);
    yield fork(watchGetFilterDistricts);
    yield fork(watchGetFilterTerritories);
    yield fork(watchGetFilterAccounts);
}
